import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import {
    ModuleDispatcher,
    ModuleSecondaryFeatureBlock
} from '$partials/modules';

import { Meta } from '$partials/global';
import { Header, Footer, StickyCTAButtons } from '$partials/global/index.js';
import Image from '$partials/media/image';
import { findModuleAnchors, getAllModules } from '$utilities/data';
import { useAnchor } from '$contexts/anchor';
import AnchorNavigation from '$partials/ui/anchor-navigation';
import { Media } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ArticleDetailPage = (props) => {
    const page = props.data.page;
    const newsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');
    const firstAccordion = page?.modules?.find((item) => item.__typename === 'AccordionBlock');

    // state
    const [selectedAccordionId, setSelectedAccordionId] = useState(firstAccordion?.id);
    const [showAllTags, setShowAllTags] = useState(false);

    // anchor
    const { setAnchors, anchors } = useAnchor();

    let anchorModules = [];
    const modules = getAllModules(page);

    useEffect(() => {
        if (modules) {
            anchorModules = anchorModules.concat(findModuleAnchors(modules));
            setAnchors(anchorModules);
        }
    }, []);

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                pageType={page?.__typename}
                standfirst={page?.standfirst} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main id='main-content'>
                <section className='layout-container mb-60 md:mb-100 mt-40 md:-mt-50'>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='sm:w-8/12 md:w-7/12 layout-grid-col-padding'>
                            {page?.parent && (
                                <p className='type-style-4 pb-20'>{page.parent.title}</p>
                            )}

                            {page?.title && (
                                <h1 className='type-style-2 pb-30'>{page.title}</h1>
                            )}

                            {page?.subtitle1 && (
                                <p className='type-style-4 pb-10'>{page.subtitle1}</p>
                            )}

                            {page?.subtitle2 && (
                                <p className='type-style-4 pb-20'>{page.subtitle2}</p>
                            )}

                            <div className='pb-40 md:pb-60 md:flex'>
                                <div className='pb-5 md:pb-0'>
                                    {page?.authors?.edges?.length > 0 && (
                                        <span className='type-style-4 mr-5'>Written by</span>
                                    )}

                                    {page?.authors?.edges.map(({node: item}, idx) => {
                                        const isLast = idx === page.authors.edges.length-1;
                                        return (
                                            <span
                                                className='type-style-4'
                                                key={idx}>
                                                <a
                                                    className='underline cursor-pointer'
                                                    href={item.url}>
                                                    {item.title}
                                                </a>

                                                {isLast ? null : <span>,&nbsp;&nbsp;</span>}
                                            </span>
                                        );
                                    })}
                                </div>
                                {page?.publicationDate && (
                                    <span className={`type-style-4${page?.authors?.edges?.length > 0 ? ' md:ml-30' : ' md:ml-5'}`}>
                                        {dayjs(page.publicationDate).format('DD MMMM YYYY')}
                                    </span>
                                )}
                            </div>

                            {page?.tags && (
                                <div className='flex flex-wrap gap-10'>
                                    {page.tags.length <= 2 && page.tags.map((item, idx) => {
                                        return (
                                            <a
                                                className='pill-button'
                                                href={item.url}
                                                key={idx}>
                                                {item.title}
                                            </a>
                                        );
                                    })}

                                    {page.tags.length > 2 && page.tags.slice(0, 2).map((item, idx) => {
                                        return (
                                            <a
                                                className='pill-button'
                                                href={item.url}
                                                key={idx}>
                                                {item.title}
                                            </a>
                                        );
                                    })}

                                    {page.tags.length > 2 && (
                                        <button
                                            className={`${showAllTags ? 'hidden' : 'pill-button'}`}
                                            onClick={() => {
                                                setShowAllTags(true);
                                            }}
                                            type='button'>
                                            ...
                                        </button>
                                    )}

                                    {showAllTags && page.tags.slice(2).map((item, idx) => {
                                        return (
                                            <a
                                                className='pill-button'
                                                href={item.url}
                                                key={idx}>
                                                {item.title}
                                            </a>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Hero Media */}
                {page?.heroMedia?.length > 0 && (
                    <div className='relative h-screen w-screen bg-white'>
                        <figure className='relative h-full w-full'>
                            <Media { ...page?.heroMedia?.[0]?.media } />
                        </figure>
                    </div>
                )}

                {/* Anchor Navigation */}
                {anchors?.length > 0 && (
                    <AnchorNavigation
                        anchors={anchors} />
                )}

                <div className='bg-white relative'>
                    {page?.modules?.map((module, idx) => {
                        if (module.__typename === 'AccordionBlock') {
                            module.isSelectedAccordion = (module.id === selectedAccordionId);
                            module.setIsSelectedAccordion = setSelectedAccordionId;
                        }

                        if (module.__typename === 'NewsletterBlock' || module.__typename === 'CalendarBlock') {
                            module.borderTop = true;
                        }

                        return (
                            <ModuleDispatcher
                                index={idx}
                                isDetailPage={true}
                                key={idx}
                                {...module} />
                        );
                    })}
                </div>

                {page?.authors?.edges?.length > 0 && (
                    <section className='relative bg-white layout-container py-40 border-t-charcoal border-t'>
                        <div className='layout-grid-negative-gutter'>
                            <div className='sm:flex sm:flex-wrap'>
                                {page.authors.edges.map(({ node: item }, idx) => {
                                    return (
                                        <React.Fragment key={idx}>
                                            <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                            <div className='w-12/12 sm:w-6/12 md:w-11/12 pb-40'>
                                                <div className='flex'>
                                                    <figure className='layout-grid-col-padding w-4/12 sm:w-2/6 md:w-2/11 pointer-events-none'>
                                                        <div className='aspect-square overflow-hidden'>
                                                            <Image
                                                                {...item.image}
                                                                alt={`Image of ${item.title}`} />
                                                        </div>
                                                    </figure>

                                                    <div className='layout-grid-col-padding sm:w-4/6 md:w-9/11'>
                                                        <p className='type-style-4 mb-20'>Written by</p>

                                                        <p className='mb-10 md:mb-30 md:flex md:flex-wrap md:items-baseline md:gap-5'>
                                                            <span className='type-style-3'>
                                                                {item.title}
                                                            </span>

                                                            <span className='type-style-4 hidden md:block'>|</span>

                                                            <span className='type-style-4 hidden md:block'>{item.role}</span>
                                                        </p>

                                                        <p className='type-style-4 md:hidden'>{item.role}</p>

                                                        <div className='hidden md:block mt-30 md:mt-0'>
                                                            <a
                                                                className='pill-button'
                                                                href={item.url}>
                                                                View Profile
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='md:hidden layout-grid-col-padding mt-25'>
                                                    <a
                                                        className='pill-button'
                                                        href={item.url}>
                                                        View Profile
                                                    </a>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                {page?.secondaryFeature?.[0] && (
                    <>
                        <div className='border-t border-t-charcoal' />
                        <ModuleSecondaryFeatureBlock {...page?.secondaryFeature?.[0]} />
                        {page?.contributors?.length > 0 && (
                            <div className='border-b border-b-charcoal' />
                        )}
                    </>
                )}

                {page?.contributors?.length > 0 && (
                    <>
                        {!page?.secondaryFeature?.[0] && (
                            <div className='bg-white layout-container'>
                                <div className='layout-grid-negative-gutter flex'>
                                    <div className='md:w-1/12' />
                                    <div className='w-12/12 md:w-11/12 layout-grid-col-padding'>
                                        <div className='border-t border-t-charcoal' />
                                    </div>
                                </div>
                            </div>
                        )}

                        <section className='relative bg-white layout-container py-40'>
                            <div className='layout-grid-negative-gutter md:flex'>
                                <div className='md:w-1/12 layout-grid-col-padding' />
                                <h2 className='type-style-4 pb-20 layout-grid-col-padding'>Contributors</h2>
                            </div>

                            <div className='layout-grid-negative-gutter'>
                                <div>
                                    {page.contributors.map((item, idx) => {
                                        return (
                                            <section
                                                className='md:flex'
                                                key={idx}>
                                                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                                <div
                                                    className='items-baseline layout-grid-col-padding md:flex pb-30 sm:w-6/12'>
                                                    <p className='type-style-3 pb-10'>{item.name}</p>

                                                    <div className='hidden md:block type-style-4'>&ensp;|&ensp;</div>

                                                    <p className='type-style-4'>{item.role}</p>
                                                </div>
                                            </section>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                    </>
                )}

                {page?.relatedModules?.length > 0 && (
                    <div className='bg-white py-40 border-t-charcoal border-t'>
                        {page?.relatedModules?.map((module, idx) => {
                            if (module.__typename === 'AccordionBlock') {
                                module.isSelectedAccordion = (module.id === selectedAccordionId);
                                module.setIsSelectedAccordion = setSelectedAccordionId;
                            }

                            if (module.__typename === 'NewsletterBlock' || module.__typename === 'CalendarBlock') {
                                module.borderTop = true;
                            }

                            return (
                                <ModuleDispatcher
                                    index={idx}
                                    isDetailPage={true}
                                    key={idx}
                                    {...module} />
                            );
                        })}
                    </div>
                )}
            </main>

            <Footer
                hasNewsletterModule={newsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        page: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ArticleDetailPage.propTypes = propTypes;

export default ArticleDetailPage;
